import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantTheme } from "@/contexts/TenantContext";
import TileSphereLogo from "@/components/TileSphereLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CustomUrlDialog from "@/components/CustomUrlDialog";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { ROLE_ROUTES, basePathForRole } from "@/lib/roleRoutes";

function landingForUser(u) {
  if (u.role === "SUPER_ADMIN") return "/admin";
  if (u.account_status !== "APPROVED") return "/pending-approval";
  // Prefer tenant-scoped URL when tenant_slug is known
  const route = ROLE_ROUTES[u.role];
  if (u.tenant_slug && route) {
    return `/${u.tenant_slug}/${route.slug}`;
  }
  return basePathForRole(u.role);
}

const DEMO_ACCOUNTS = [
  ["Org Admin",            "org-admin@stonewave.example",          "Demo@123",  "Stonewave Buyers — full org access"],
  ["Procurement Manager",  "procurement@stonewave.example",        "Demo@123",  "Requisitions / POs / GRN"],
  ["Sales Manager",        "sales-manager@stonewave.example",      "Demo@123",  "Leads / quotes / orders / targets"],
  ["Sales Executive",      "sales-exec1@stonewave.example",        "Demo@123",  "Field rep — own leads & visits"],
  ["Distributor",          "distributor@stonewave.example",        "Demo@123",  "Regional ops, logistics, BI"],
  ["Warehouse Manager",    "warehouse@stonewave.example",          "Demo@123",  "GRN / pick / pack / dispatch / barcode"],
  ["Dealer",               "dealer@morbihub.example",              "Demo@123",  "Catalogue, cart, Stripe checkout"],
  ["Vendor Admin",         "demo-vendor@tilesphere.com",            "Demo@123",  "Claymont Ceramics — vendor portal"],
  ["Buyer (marketplace)",  "demo-buyer@tilesphere.com",             "Demo@123",  "B2B marketplace browsing"],
];

// Demo accounts are the ONLY tenant users allowed to sign in from the generic /login page.
// This lets prospects try the product end-to-end without having to remember or type a
// customized tenant URL. Real (registered/approved) tenant users still get the wrong-URL
// dialog so they learn to bookmark their own portal.
const DEMO_EMAILS = new Set(DEMO_ACCOUNTS.map(([, email]) => email.toLowerCase()));

export default function Login({ hideDemo = false, accentLabel = null, enforceSlug = null, enforceRole = null }) {
  const { t } = useTranslation();
  const { login, verifyMfaLogin } = useAuth();
  const { tenant: brandTenant } = useTenantTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mfa, setMfa] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [urlHint, setUrlHint] = useState(null);   // { custom_url, user } — shows the CustomUrlDialog
  const [forgotOpen, setForgotOpen] = useState(false);

  const tenantName = brandTenant?.branding?.company_name || brandTenant?.name;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    // Demo accounts bypass the wrong-URL gate — they're built for exploratory sign-in from /login.
    const isDemo = DEMO_EMAILS.has((email || "").trim().toLowerCase());
    const ctx = isDemo ? { bypass: true } : { enforceSlug, enforceRole };
    const res = await login(email, password, ctx);
    setBusy(false);
    if (res.wrong_url) {
      // Tenant user tried to sign in from the wrong URL. Show the custom URL + copy.
      setUrlHint({ custom_url: res.custom_url, user: res.user });
      return;
    }
    if (!res.ok) { setError(res.error); toast.error(res.error); return; }
    if (res.requires_mfa) { setMfa({ mfa_token: res.mfa_token, email: res.email }); toast.message(t("auth.twoFactorNeeded")); return; }
    toast.success(t("auth.welcomeBack"));
    navigate(landingForUser(res.user));
  };

  const onMfaSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await verifyMfaLogin(mfa.mfa_token, mfaCode);
    setBusy(false);
    if (!res.ok) { setError(res.error); toast.error(res.error); return; }
    toast.success(t("auth.welcomeBack"));
    navigate(landingForUser(res.user));
  };

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white relative overflow-hidden" data-testid="login-page">
      {/* ambient glow backdrop */}
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-orange-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-fuchsia-600/15 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.04),transparent_60%)] pointer-events-none" />

      {/* top nav */}
      <header className="relative z-10 px-6 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <TileSphereLogo />
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="dark" />
          <Link to="/">
            <Button variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" data-testid="back-home">
              <ArrowLeft className="w-4 h-4 mr-1" /> {t("auth.backHome")}
            </Button>
          </Link>
        </div>
      </header>

      <div className="relative z-10 grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto px-6 pb-16 pt-4">
        {/* LEFT — Pitch column */}
        <div className="hidden lg:flex flex-col justify-center" data-testid="login-pitch">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-[11px] font-bold uppercase tracking-[0.25em] text-orange-300 w-fit">
            <Sparkles className="w-3 h-3" /> {t("auth.ecoTag")}
          </div>
          <h1 className="font-display text-5xl xl:text-6xl font-black mt-5 leading-[0.95]">
            {brandTenant ? (
              <>{t("auth.welcomeToPrefix")}{" "}<span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">{tenantName}</span>.</>
            ) : (
              <>{t("auth.heroTitle")} <br /><span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">{t("auth.heroTitleAccent")}</span></>
            )}
          </h1>
          <p className="mt-5 text-white/60 text-lg max-w-md">{t("auth.heroSub")}</p>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
            {[
              { k: t("auth.stat1Value"), v: t("auth.stat1Label") },
              { k: t("auth.stat2Value"), v: t("auth.stat2Label") },
              { k: t("auth.stat3Value"), v: t("auth.stat3Label") },
            ].map((s) => (
              <div key={s.v} className="p-4 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur">
                <div className="font-display text-2xl font-black bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">{s.k}</div>
                <div className="text-[11px] text-white/50 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Form column */}
        <div className="flex items-start justify-center lg:justify-end">
          <div className="w-full max-w-md">
            {!mfa ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-8 shadow-2xl shadow-black/40">
                <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                  <div>
                    <h2 className="font-display text-2xl sm:text-3xl font-black text-white">{t("auth.signInTitle")}</h2>
                    <p className="text-sm text-white/50 mt-1">
                      {accentLabel
                        ? accentLabel
                        : brandTenant ? <>{t("auth.signInSubTenant", { tenant: tenantName })}</> : t("auth.signInSubDefault")}
                    </p>
                  </div>

                  {error ? (
                    <div className="text-sm rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 px-3 py-2" data-testid="login-error">{error}</div>
                  ) : null}

                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-white/70 text-xs uppercase tracking-wide">{t("auth.emailLabel")}</Label>
                    <Input
                      id="email" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} required
                      data-testid="login-email" placeholder={t("auth.emailPlaceholder")}
                      className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-orange-500/50"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-white/70 text-xs uppercase tracking-wide">{t("auth.passwordLabel")}</Label>
                      <button
                        type="button"
                        onClick={() => setForgotOpen(true)}
                        className="text-[11px] text-orange-300 hover:text-orange-200 font-semibold"
                        data-testid="forgot-password-link"
                      >
                        {t("auth.forgotPassword")}
                      </button>
                    </div>
                    <Input
                      id="password" type="password" value={password}
                      onChange={(e) => setPassword(e.target.value)} required
                      data-testid="login-password" placeholder="••••••••"
                      className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-orange-500/50"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white font-bold rounded-xl"
                    disabled={busy} data-testid="login-submit"
                  >
                    {busy ? t("auth.signingIn") : <>{t("auth.signInBtn")} <ArrowRight className="w-4 h-4 ml-1" /></>}
                  </Button>

                </form>
              </div>
            ) : (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-8 shadow-2xl shadow-black/40">
                <form onSubmit={onMfaSubmit} className="space-y-5" data-testid="mfa-challenge-form">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-orange-300" />
                    </div>
                    <div>
                      <h2 className="font-display text-2xl font-black">{t("auth.twoFactorRequired")}</h2>
                      <p className="text-xs text-white/50 mt-0.5">{t("auth.codeFor")} <code className="font-mono text-white/80">{mfa.email}</code></p>
                    </div>
                  </div>
                  {error ? (
                    <div className="text-sm rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 px-3 py-2" data-testid="mfa-error">{error}</div>
                  ) : null}
                  <div className="space-y-1.5">
                    <Label className="text-white/70 text-xs uppercase tracking-wide">{t("auth.authCodeLabel")}</Label>
                    <Input
                      value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} maxLength={6} required
                      data-testid="mfa-code-challenge-input" placeholder="123 456" autoFocus
                      className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-orange-500/50 text-center font-mono text-2xl tracking-[0.4em]"
                    />
                  </div>
                  <Button
                    type="submit" disabled={busy || mfaCode.length < 6}
                    className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:opacity-90 text-white font-bold rounded-xl"
                    data-testid="mfa-verify-btn"
                  >
                    {busy ? t("auth.verifying") : t("auth.verifyAndSignIn")}
                  </Button>
                  <button type="button" onClick={() => { setMfa(null); setMfaCode(""); }} className="text-sm text-white/50 hover:text-white w-full text-center">
                    {t("auth.cancelUseAnother")}
                  </button>
                </form>
              </div>
            )}

            {/* Demo accounts panel */}
            {!mfa && !hideDemo ? (
              <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5" data-testid="login-hint">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-[0.25em] text-orange-300 font-bold">{t("auth.demoTitle")}</span>
                  <span className="text-[10px] text-white/40 font-normal">{t("auth.demoPasswordLabel")}: <code className="font-mono text-white/70">Demo@123</code></span>
                </div>
                <div className="grid sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto pr-1">
                  {DEMO_ACCOUNTS.map(([role, em, pw, hint]) => (
                    <button
                      type="button"
                      key={em}
                      onClick={() => { setEmail(em); setPassword(pw); }}
                      className="text-left rounded-xl px-3 py-2 hover:bg-white/5 border border-transparent hover:border-white/10 transition-all group"
                      data-testid={`demo-${role.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                      <div className="font-semibold text-white text-sm truncate">{role}</div>
                      <div className="font-mono text-[10px] text-white/40 truncate group-hover:text-orange-300">{em}</div>
                      <div className="text-[10px] text-white/40 truncate italic mt-0.5">{hint}</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <CustomUrlDialog
        open={!!urlHint}
        customUrl={urlHint?.custom_url}
        userEmail={urlHint?.user?.email}
        userRole={urlHint?.user?.role}
        onClose={() => setUrlHint(null)}
      />
      <ForgotPasswordDialog
        open={forgotOpen}
        initialEmail={email}
        onClose={() => setForgotOpen(false)}
        onResetComplete={({ email: e, password: p }) => {
          setEmail(e);
          setPassword(p);
          toast.message("Password updated — click Sign in to continue.");
        }}
      />
    </div>
  );
}
